/**
 * Iter 161 · CFO Scenario Planner — "Hire vs. Hold" simulator.
 *
 * Interactive planning tool that lets founders add hypothetical
 * adjustments (hires, revenue delays, one-off spends) and see the
 * projected 12-month cash runway curve side-by-side with the baseline.
 *
 * Route: /app/finance/scenario-planner
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer,
  Legend, ReferenceLine,
} from "recharts";
import {
  Plus, Trash2, Save, GitCompare, Sparkles, TrendingDown, TrendingUp,
  RotateCw, Users, Wallet, Calendar, X, Star,
} from "lucide-react";

function inr(n) {
  const v = Number(n || 0);
  const abs = Math.abs(v);
  const sign = v < 0 ? "-" : "";
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)}Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)}L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)}K`;
  return `${sign}₹${abs.toFixed(0)}`;
}

const KIND_LABELS = {
  hire: "Hire (recurring cost)",
  headcount_cut: "Headcount cut (recurring save)",
  recurring_expense: "New recurring expense",
  recurring_income: "New recurring income",
  one_off_spend: "One-off spend",
  one_off_income: "One-off income",
  revenue_delay: "Revenue delay",
};

const emptyAdj = () => ({
  kind: "hire",
  label: "New lever",
  monthly_amount: 150000,
  one_off_amount: 0,
  start_month_offset: 0,
  duration_months: null,
});

// Normalise API error payloads to a single-line string so `toast.error`
// never receives an array/object (which would crash React via sonner).
function asMsg(err, fallback) {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d[0]?.msg || fallback;
  if (d && typeof d === "object") return d.msg || fallback;
  return fallback;
}

export default function ScenarioPlanner() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [baselineMonths, setBaselineMonths] = useState(3);
  const [horizonMonths, setHorizonMonths] = useState(12);
  const [adjustments, setAdjustments] = useState([]);
  const [sim, setSim] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState([]);
  const [saveDlg, setSaveDlg] = useState(false);
  const [scenName, setScenName] = useState("");
  const [scenDesc, setScenDesc] = useState("");
  const [activeId, setActiveId] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [compareA, setCompareA] = useState("");
  const [compareB, setCompareB] = useState("");
  const [compareData, setCompareData] = useState(null);

  const simulate = useCallback(async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/cfo/scenario/simulate`,
        { baseline_months: baselineMonths,
          horizon_months: horizonMonths,
          adjustments,
        },
      );
      setSim(data);
    } catch (e) {
      toast.error(asMsg(e, "Simulation failed"));
    } finally { setLoading(false); }
  }, [activeWorkspaceId, baselineMonths, horizonMonths, adjustments]);

  const loadSaved = useCallback(async () => {
    if (!activeWorkspaceId) return;
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/cfo/scenarios`);
      setSaved(data.rows || []);
    } catch { /* ignore */ }
  }, [activeWorkspaceId]);

  useEffect(() => { simulate(); }, [simulate]);
  useEffect(() => { loadSaved(); }, [loadSaved]);

  const addAdj = () => setAdjustments((prev) => [...prev, emptyAdj()]);
  const updateAdj = (i, patch) =>
    setAdjustments((prev) => prev.map((a, j) => (j === i ? { ...a, ...patch } : a)));
  const removeAdj = (i) => setAdjustments((prev) => prev.filter((_, j) => j !== i));

  const saveScenario = async () => {
    if (!scenName.trim()) { toast.error("Give it a name first"); return; }
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/cfo/scenarios`,
        { name: scenName, description: scenDesc,
          baseline_months: baselineMonths,
          horizon_months: horizonMonths,
          adjustments, starred: false },
      );
      setSaved((prev) => [data, ...prev]);
      setActiveId(data.id);
      setSaveDlg(false);
      setScenName(""); setScenDesc("");
      toast.success(`"${data.name}" saved 🎯`);
    } catch (e) { toast.error(asMsg(e, "Save failed")); }
  };

  const loadScenario = (s) => {
    setAdjustments(s.adjustments || []);
    setBaselineMonths(s.baseline_months);
    setHorizonMonths(s.horizon_months);
    setActiveId(s.id);
    toast.success(`Loaded "${s.name}"`);
  };

  const deleteScenario = async (s) => {
    if (!confirm(`Delete "${s.name}"?`)) return;
    try {
      await api.delete(`/cfo/scenarios/${s.id}`);
      setSaved((prev) => prev.filter((x) => x.id !== s.id));
      if (activeId === s.id) setActiveId(null);
      toast.success("Deleted");
    } catch (e) { toast.error(asMsg(e, "Delete failed")); }
  };

  const toggleStar = async (s) => {
    try {
      const { data } = await api.patch(`/cfo/scenarios/${s.id}`,
        { starred: !s.starred });
      setSaved((prev) => prev.map((x) => (x.id === s.id ? data : x)));
    } catch { /* ignore */ }
  };

  const runCompare = async () => {
    if (!compareA || !compareB) { toast.error("Pick two scenarios"); return; }
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/cfo/scenarios/compare?a=${compareA}&b=${compareB}`);
      setCompareData(data);
    } catch (e) { toast.error(asMsg(e, "Compare failed")); }
  };

  const mergedCurve = useMemo(() => {
    if (!sim) return [];
    return sim.baseline_curve.map((b, i) => ({
      month: b.month,
      Baseline: b.cash,
      Scenario: sim.scenario_curve[i]?.cash ?? null,
    }));
  }, [sim]);

  const runwayDelta = useMemo(() => {
    if (!sim) return null;
    const b = sim.summary.baseline_runway_months;
    const s = sim.summary.scenario_runway_months;
    if (b == null && s == null) return { text: "Both scenarios stay above zero cash — profitable trajectory.", tone: "green" };
    if (b == null && s != null) return { text: `This plan burns cash — runway ends month ${s}. Baseline stays healthy.`, tone: "red" };
    if (b != null && s == null) return { text: `This plan pulls you back to profitability — baseline exhausts at month ${b}.`, tone: "green" };
    const delta = s - b;
    if (delta > 0) return { text: `Extends runway by ${delta} month(s) (${b} → ${s})`, tone: "green" };
    if (delta < 0) return { text: `Shortens runway by ${-delta} month(s) (${b} → ${s})`, tone: "red" };
    return { text: "No net runway change — try different levers.", tone: "amber" };
  }, [sim]);

  return (
    <div data-testid="scenario-planner" className="p-6 md:p-8 space-y-6 min-h-screen">
      <PageHeader
        title="Scenario Planner"
        subtitle="Hire vs. hold, delays, one-offs — see runway impact live."
      />

      {/* Controls row */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        <div className="rounded-xl border border-border bg-card p-4 lg:col-span-3">
          <div className="flex items-center gap-3 mb-4">
            <Sparkles className="h-4 w-4" style={{ color: "hsl(var(--primary))" }} />
            <h3 className="font-bold text-sm">Adjustments</h3>
            <Button size="sm" variant="outline" onClick={addAdj} className="ml-auto"
                     data-testid="add-adjustment-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Add lever
            </Button>
          </div>
          {adjustments.length === 0 && (
            <div className="text-sm text-muted-foreground py-4 text-center border-2 border-dashed rounded-lg">
              No levers yet — add a hire, a revenue delay, or a one-off spend to model the impact.
            </div>
          )}
          <div className="space-y-2" data-testid="adjustments-list">
            {adjustments.map((adj, i) => (
              <AdjustmentRow key={i} idx={i} adj={adj}
                             onChange={(patch) => updateAdj(i, patch)}
                             onRemove={() => removeAdj(i)} />
            ))}
          </div>

          <div className="mt-4 pt-4 border-t border-border flex items-center gap-3">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Baseline</Label>
            <Select value={String(baselineMonths)} onValueChange={(v) => setBaselineMonths(Number(v))}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="baseline-months">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="3">Trailing 3 mo</SelectItem>
                <SelectItem value="6">Trailing 6 mo</SelectItem>
              </SelectContent>
            </Select>
            <Label className="text-xs uppercase tracking-widest text-muted-foreground ml-3">Horizon</Label>
            <Select value={String(horizonMonths)} onValueChange={(v) => setHorizonMonths(Number(v))}>
              <SelectTrigger className="w-32 h-8 text-xs" data-testid="horizon-months">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="6">6 months</SelectItem>
                <SelectItem value="12">12 months</SelectItem>
                <SelectItem value="18">18 months</SelectItem>
                <SelectItem value="24">24 months</SelectItem>
              </SelectContent>
            </Select>
            <Button size="sm" variant="outline" onClick={simulate} disabled={loading}
                     className="ml-auto" data-testid="simulate-btn">
              <RotateCw className={`h-3.5 w-3.5 mr-1 ${loading ? "animate-spin" : ""}`} />
              Re-simulate
            </Button>
            <Button size="sm" onClick={() => setSaveDlg(true)} data-testid="save-scenario-btn">
              <Save className="h-3.5 w-3.5 mr-1" /> Save…
            </Button>
          </div>
        </div>

        {/* Saved list */}
        <div className="rounded-xl border border-border bg-card p-4" data-testid="saved-scenarios">
          <div className="flex items-center gap-2 mb-3">
            <Star className="h-4 w-4 text-amber-500" />
            <h3 className="font-bold text-sm">Saved plans</h3>
            {saved.length >= 2 && (
              <Button size="sm" variant="outline" className="ml-auto"
                       onClick={() => setCompareOpen(true)}
                       data-testid="compare-btn">
                <GitCompare className="h-3.5 w-3.5 mr-1" /> Compare
              </Button>
            )}
          </div>
          {saved.length === 0 && (
            <div className="text-xs text-muted-foreground py-2">
              Save a plan to compare A/B later.
            </div>
          )}
          <div className="space-y-1.5">
            {saved.map((s) => (
              <div key={s.id}
                   className={`text-xs rounded-md border p-2 flex items-center gap-2 hover:bg-muted/30 ${activeId === s.id ? "border-primary bg-primary/5" : "border-border"}`}
                   data-testid={`saved-scenario-${s.id}`}>
                <button type="button" onClick={() => toggleStar(s)}
                         data-testid={`star-${s.id}`}>
                  <Star className={`h-3 w-3 ${s.starred ? "fill-amber-400 text-amber-500" : "text-muted-foreground"}`} />
                </button>
                <button type="button" onClick={() => loadScenario(s)}
                         className="flex-1 text-left truncate font-medium">
                  {s.name}
                </button>
                <span className="text-muted-foreground font-mono text-[10px]">
                  {(s.adjustments || []).length}L
                </span>
                <button type="button" onClick={() => deleteScenario(s)}
                         className="text-muted-foreground hover:text-red-500"
                         data-testid={`del-${s.id}`}>
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Summary strip */}
      {sim && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-strip">
          <SummaryTile label="Starting cash" value={inr(sim.baseline?.cash_on_hand)}
                       icon={<Wallet className="h-4 w-4 text-emerald-600" />} />
          <SummaryTile label="Baseline burn / mo" value={inr(sim.baseline?.net_burn)}
                       icon={<TrendingDown className="h-4 w-4 text-red-500" />} />
          <SummaryTile label="Baseline runway"
                       value={sim.summary.baseline_runway_months == null ? "∞" : `${sim.summary.baseline_runway_months} mo`}
                       icon={<Calendar className="h-4 w-4" />} />
          <SummaryTile label="Scenario runway"
                       value={sim.summary.scenario_runway_months == null ? "∞" : `${sim.summary.scenario_runway_months} mo`}
                       tone={runwayDelta?.tone}
                       icon={<TrendingUp className="h-4 w-4" />} />
        </div>
      )}

      {/* Chart */}
      {sim && (
        <div className="rounded-xl border border-border bg-card p-5" data-testid="scenario-chart">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-bold text-sm">Projected cash balance</h3>
            {runwayDelta && (
              <span className={`ml-auto text-xs font-semibold ${
                runwayDelta.tone === "green" ? "text-emerald-700"
                  : runwayDelta.tone === "red" ? "text-red-600" : "text-amber-600"
              }`} data-testid="runway-delta-text">
                {runwayDelta.text}
              </span>
            )}
          </div>
          <ResponsiveContainer width="100%" height={320}>
            <LineChart data={mergedCurve}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={inr} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="4 4" label={{ value: "Zero cash", fontSize: 10, fill: "#dc2626" }} />
              <Line type="monotone" dataKey="Baseline" stroke="#94a3b8"
                    strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="Scenario" stroke="hsl(var(--primary))"
                    strokeWidth={3} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Save dialog */}
      <Dialog open={saveDlg} onOpenChange={setSaveDlg}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save this scenario</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Name</Label>
              <Input value={scenName} onChange={(e) => setScenName(e.target.value)}
                     placeholder="e.g. Hire 2 engineers Q3"
                     data-testid="scen-name-input" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={scenDesc} onChange={(e) => setScenDesc(e.target.value)}
                     placeholder="Assumptions or notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDlg(false)}>Cancel</Button>
            <Button onClick={saveScenario} data-testid="scen-save-confirm">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compare dialog */}
      <Dialog open={compareOpen} onOpenChange={setCompareOpen}>
        <DialogContent className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>A/B compare scenarios</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scenario A</Label>
              <Select value={compareA} onValueChange={setCompareA}>
                <SelectTrigger data-testid="compare-a"><SelectValue placeholder="Pick A" /></SelectTrigger>
                <SelectContent>
                  {saved.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Scenario B</Label>
              <Select value={compareB} onValueChange={setCompareB}>
                <SelectTrigger data-testid="compare-b"><SelectValue placeholder="Pick B" /></SelectTrigger>
                <SelectContent>
                  {saved.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex justify-end mt-2">
            <Button size="sm" onClick={runCompare} data-testid="compare-run">Run compare</Button>
          </div>
          {compareData && (
            <CompareChart data={compareData} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, icon, tone }) {
  const toneCls = tone === "green" ? "text-emerald-700"
    : tone === "red" ? "text-red-600"
    : tone === "amber" ? "text-amber-700" : "";
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1.5">
        {icon} {label}
      </div>
      <div className={`mt-1 text-2xl font-black font-mono ${toneCls}`}>{value}</div>
    </div>
  );
}

function AdjustmentRow({ idx, adj, onChange, onRemove }) {
  const isRecurring = ["hire", "headcount_cut", "recurring_expense",
                       "recurring_income", "revenue_delay"].includes(adj.kind);
  const isOneOff = ["one_off_spend", "one_off_income"].includes(adj.kind);
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border p-3"
         data-testid={`adj-row-${idx}`}>
      <div className="min-w-[180px] flex-1">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Type</Label>
        <Select value={adj.kind} onValueChange={(v) => onChange({ kind: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`adj-kind-${idx}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Object.entries(KIND_LABELS).map(([k, v]) =>
              <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="min-w-[200px] flex-1">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Label</Label>
        <Input value={adj.label} onChange={(e) => onChange({ label: e.target.value })}
               placeholder="e.g. Senior engineer"
               className="h-8 text-xs" data-testid={`adj-label-${idx}`} />
      </div>
      {isRecurring && (
        <div className="w-32">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">₹/month</Label>
          <Input type="number" value={adj.monthly_amount}
                 onChange={(e) => onChange({ monthly_amount: Number(e.target.value) })}
                 className="h-8 text-xs font-mono" data-testid={`adj-monthly-${idx}`} />
        </div>
      )}
      {isOneOff && (
        <div className="w-32">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Amount ₹</Label>
          <Input type="number" value={adj.one_off_amount}
                 onChange={(e) => onChange({ one_off_amount: Number(e.target.value) })}
                 className="h-8 text-xs font-mono" data-testid={`adj-oneoff-${idx}`} />
        </div>
      )}
      <div className="w-24">
        <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Start (mo)</Label>
        <Input type="number" min="0" value={adj.start_month_offset}
               onChange={(e) => onChange({ start_month_offset: Number(e.target.value) })}
               className="h-8 text-xs font-mono" data-testid={`adj-start-${idx}`} />
      </div>
      {isRecurring && (
        <div className="w-24">
          <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Duration</Label>
          <Input type="number" placeholder="∞" min="1"
                 value={adj.duration_months || ""}
                 onChange={(e) => onChange({ duration_months: e.target.value ? Number(e.target.value) : null })}
                 className="h-8 text-xs font-mono" data-testid={`adj-duration-${idx}`} />
        </div>
      )}
      <Button size="sm" variant="ghost" onClick={onRemove}
              data-testid={`adj-remove-${idx}`}>
        <X className="h-3.5 w-3.5 text-red-500" />
      </Button>
    </div>
  );
}

function CompareChart({ data }) {
  const rows = (data.a.sim.baseline_curve || []).map((b, i) => ({
    month: b.month,
    [data.a.scenario.name]: data.a.sim.scenario_curve[i]?.cash,
    [data.b.scenario.name]: data.b.sim.scenario_curve[i]?.cash,
    Baseline: b.cash,
  }));
  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid="compare-chart">
      <div className="grid grid-cols-2 gap-3 text-xs mb-3">
        <div className="rounded-md border border-border p-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">A · {data.a.scenario.name}</div>
          <div className="font-mono font-bold">Runway: {data.a.sim.scenario_runway_months == null ? "∞" : `${data.a.sim.scenario_runway_months} mo`}</div>
        </div>
        <div className="rounded-md border border-border p-2">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">B · {data.b.scenario.name}</div>
          <div className="font-mono font-bold">Runway: {data.b.sim.scenario_runway_months == null ? "∞" : `${data.b.sim.scenario_runway_months} mo`}</div>
        </div>
      </div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={rows}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.35} />
          <XAxis dataKey="month" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} tickFormatter={inr} />
          <Tooltip formatter={(v) => inr(v)} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <ReferenceLine y={0} stroke="#dc2626" strokeDasharray="4 4" />
          <Line type="monotone" dataKey="Baseline" stroke="#94a3b8" strokeWidth={2} dot={false} />
          <Line type="monotone" dataKey={data.a.scenario.name} stroke="#3b82f6" strokeWidth={2.5} />
          <Line type="monotone" dataKey={data.b.scenario.name} stroke="#f97316" strokeWidth={2.5} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}
